import {
  PRESALE_CREATOR_MAX_PCT,
  PRESALE_LP_DEFAULT_PCT,
  PRESALE_LP_MAX_PCT,
  PRESALE_LP_MIN_PCT,
  clampPresaleLpPct,
  creatorPctFromLp,
  formatBps,
  PRESALE_FEE_BPS,
} from '@/lib/protocol'

interface PresaleProceedsSliderProps {
  lpPct: number
  onChange?: (lpPct: number) => void
  raisedEth?: number
  readOnly?: boolean
}

function PresaleProceedsSlider({
  lpPct,
  onChange,
  raisedEth,
  readOnly = false,
}: PresaleProceedsSliderProps) {
  const clamped = clampPresaleLpPct(lpPct)
  const creatorPct = creatorPctFromLp(clamped)
  const remainingAfterFee = 100 - PRESALE_FEE_BPS / 100
  const previewRaised = raisedEth && raisedEth > 0 ? raisedEth : 10
  const platformEth = previewRaised * (PRESALE_FEE_BPS / 10_000)
  const netEth = previewRaised - platformEth
  const lpEth = netEth * (clamped / 100)
  const creatorEth = netEth * (creatorPct / 100)

  return (
    <div className="rounded-2xl border border-orange-400/20 bg-orange-400/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-400">
            ETH proceeds
          </p>
          <p className="mt-1 text-sm font-medium text-white">
            Liquidity vs creator
          </p>
        </div>
        <p className="text-right text-xs text-zinc-500">
          After
          {' '}
          {formatBps(PRESALE_FEE_BPS)}
          {' '}
          platform fee
        </p>
      </div>

      <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-zinc-900" aria-hidden="true">
        <div className="bg-orange-300" style={{ width: `${clamped}%` }} />
        <div className="bg-orange-500" style={{ width: `${creatorPct}%` }} />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="font-medium text-orange-300">
          LP
          {' '}
          {clamped}
          %
        </span>
        <span className="font-medium text-orange-200">
          Creator
          {' '}
          {creatorPct}
          %
        </span>
      </div>

      {readOnly
        ? null
        : (
            <label className="mt-4 block">
              <span className="sr-only">Liquidity pool percent of remaining ETH</span>
              <input
                type="range"
                min={PRESALE_LP_MIN_PCT}
                max={PRESALE_LP_MAX_PCT}
                step={1}
                value={clamped}
                onChange={event => onChange?.(clampPresaleLpPct(Number(event.target.value)))}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-zinc-800 accent-orange-400"
              />
            </label>
          )}

      <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
        LP must stay between
        {' '}
        {PRESALE_LP_MIN_PCT}
        % and
        {' '}
        {PRESALE_LP_MAX_PCT}
        %. Creator can take at most
        {' '}
        {PRESALE_CREATOR_MAX_PCT}
        % of remaining ETH (default
        {' '}
        {PRESALE_LP_DEFAULT_PCT}
        /
        {100 - PRESALE_LP_DEFAULT_PCT}
        ). Penguin Pad takes
        {' '}
        {formatBps(PRESALE_FEE_BPS)}
        {' '}
        of ETH raised first —
        {' '}
        {remainingAfterFee}
        % is then split on this meter.
      </p>

      <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl border border-white/10 bg-black/40 px-2 py-2">
          <dt className="text-[10px] uppercase tracking-wider text-zinc-500">Platform</dt>
          <dd className="mt-0.5 text-xs font-semibold text-white">
            {platformEth.toFixed(3)}
            {' '}
            ETH
          </dd>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/40 px-2 py-2">
          <dt className="text-[10px] uppercase tracking-wider text-zinc-500">LP</dt>
          <dd className="mt-0.5 text-xs font-semibold text-orange-300">
            {lpEth.toFixed(3)}
            {' '}
            ETH
          </dd>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/40 px-2 py-2">
          <dt className="text-[10px] uppercase tracking-wider text-zinc-500">Creator</dt>
          <dd className="mt-0.5 text-xs font-semibold text-orange-200">
            {creatorEth.toFixed(3)}
            {' '}
            ETH
          </dd>
        </div>
      </dl>
      {raisedEth && raisedEth > 0
        ? (
            <p className="mt-2 text-center text-[10px] text-zinc-600">
              Split of
              {' '}
              {previewRaised}
              {' '}
              ETH raised (after fee).
            </p>
          )
        : (
            <p className="mt-2 text-center text-[10px] text-zinc-600">
              Preview assumes
              {' '}
              {previewRaised}
              {' '}
              ETH raised.
            </p>
          )}
    </div>
  )
}

export default PresaleProceedsSlider
